<template>
  <section class="report-catalog q-pa-md">
    <header class="report-catalog__heading">
      <div>
        <h1>{{ $t('reportCatalog.title') }}</h1>
      </div>
      <q-btn v-if="isAdministrator" outline color="primary" icon="add" no-caps
        :label="$t('reportCatalog.add')" @click="handleAdd" />
    </header>

    <div class="report-catalog__panel">
      <div class="report-catalog__toolbar">
        <q-tabs v-model="tab" class="report-catalog__tabs" active-color="primary"
          indicator-color="primary" align="left" narrow-indicator outside-arrows>
          <q-tab name="all" :label="$t('reportCatalog.all')" no-caps />
          <q-tab v-for="category in catalogueCategories" :key="category.id"
            :name="category.id" :label="categoryLabel(category)" no-caps />
          <q-tab v-if="reportAccess.operations || reportAccess.reportTypes" name="requested" :label="$t('reportParity.onRequest')" no-caps />
        </q-tabs>
        <q-input v-model="search" outlined dense clearable class="report-catalog__search"
          :placeholder="$t('reportCatalog.search')" :aria-label="$t('reportCatalog.search')">
          <template #prepend><q-icon name="search" /></template>
        </q-input>
      </div>
      <q-separator />
      <div class="report-catalog__content" :aria-busy="isLoading">
        <q-banner v-if="loadError" class="bg-red-1 text-negative q-mb-md" role="alert">
          {{ $t('reportCatalog.loadError') }}
          <template #action><q-btn flat no-caps :label="$t('reportCatalog.retry')" @click="reload" /></template>
        </q-banner>
        <div v-if="visibleItems.length" class="report-catalog__grid">
          <article v-for="item in visibleItems" :key="item.id" class="report-card">
            <button type="button" class="report-card__link" @click="handleSelect(item)">
              <div class="report-card__top">
                <span class="report-card__icon"><q-icon :name="reportIcon(item)" size="25px" /></span>
                <span class="report-card__type">{{ $t(!item.builtin || item.id === 'requested-reports' ? 'reportParity.onRequest' : 'reportCatalog.interactive') }}</span>
              </div>
              <h2>{{ reportTitle(item) }}</h2>
              <p>{{ reportDescription(item) }}</p>
              <span class="report-card__action">{{ $t('reportCatalog.open') }}<q-icon name="arrow_forward" size="18px" /></span>
            </button>
            <q-btn v-if="isAdministrator && !item.builtin" flat dense icon="edit" class="report-card__edit"
              :aria-label="$t('reportCatalog.edit', { name: reportTitle(item) })" @click="handleOpen(item)" />
          </article>
        </div>
        <div v-if="isLoading" class="report-catalog__status" role="status">
          <q-spinner color="primary" size="28px" /><span>{{ $t('reportCatalog.loading') }}</span>
        </div>
        <div v-else-if="!visibleItems.length && !loadError" class="report-catalog__empty" role="status">
          <q-icon :name="search ? 'search_off' : 'folder_open'" size="40px" />
          <h2>{{ $t('reportCatalog.empty') }}</h2>
          <p>{{ $t(search ? 'reportCatalog.emptySearch' : 'reportCatalog.emptyCategory') }}</p>
          <q-btn v-if="search" flat color="primary" no-caps :label="$t('reportCatalog.clearSearch')" @click="search = ''" />
        </div>
      </div>
    </div>
    <report-type-modal ref="reportTypeModal" @submit="reload" />
  </section>
</template>

<script>
// Lazy catalogue: the API is used after mounting.
import { HALApiService } from '../../services/hal-api.service.js';
import { mapGetters, mapMutations } from 'vuex';
// Lazy routes use the initialized HTTP client only after mounting.
import { employeePerformanceService } from '../../services/requests/employee-performance.service.js';
import ReportTypeModal from '../../components/modals/ReportTypeModal.vue';
import { permissions } from '../../../../services/permissions.service.js';
import { reportAccess } from '../../helpers/report-access.js';
import { reportDefinition, categoryKey } from '../../helpers/report-catalog.js';

export default {
  name: 'ReportCategories',
  components: { ReportTypeModal },
  data() {
    return { categories: [], items: [], diagnosticsEnabled: false, employeePerformanceEnabled: false, isLoading: false,
      loadError: false, search: '', tab: 'all', requestId: 0 };
  },
  computed: {
    ...mapGetters(['isAdministrator']),
    reportAccess() { return reportAccess(permissions.getList()); },
    catalogueCategories() {
      const categories = [...this.categories];
      for (const key of ['operations', 'finance']) {
        if (!categories.some((category) => categoryKey(category.name) === key)) {
          categories.push({ id: `builtin-${key}`, name: key === 'operations' ? 'Warehouse operations' : 'Finance' });
        }
      }
      return categories;
    },
    visibleItems() {
      const category = this.catalogueCategories.find((item) => item.id === this.tab);
      const selected = category && categoryKey(category.name);
      const builtins = [
        { id: 'operations', category: 'operations', icon: 'query_stats' },
        { id: 'billing-report', category: 'finance', icon: 'receipt_long' },
        { id: 'balance-report', category: 'finance', icon: 'account_balance_wallet' },
      ];
      if (this.tab === 'requested' && this.reportAccess.operations) builtins.push({ id: 'requested-reports', category: 'requested', icon: 'folder_open' });
      if (this.diagnosticsEnabled) builtins.push({ id: 'billing-diagnostics', category: 'finance', icon: 'assessment' });
      if (this.employeePerformanceEnabled) builtins.unshift({ id: 'employee-performance', category: 'operations', icon: 'groups' });
      const items = [...builtins.filter((item) => (item.id === 'requested-reports' ? this.reportAccess.operations : this.reportAccess[item.id]) && (this.tab === 'all' || selected === item.category || (this.tab === 'requested' && item.category === 'requested')))
        .map((item) => ({ ...item, builtin: true, type: 'dynamic' })), ...this.items];
      const search = (this.search || '').trim().toLocaleLowerCase();
      return items.filter((item) => !search || [this.reportTitle(item), this.reportDescription(item), item.name || '']
        .join(' ').toLocaleLowerCase().includes(search));
    },
  },
  watch: { tab() { this.loadItems(); } },
  mounted() {
    HALApiService.get('/reports/statistics/accounts/billing-diagnostics/options', {}).then((data) => { this.diagnosticsEnabled = data.available === true; }).catch(() => {});
    this.reload();
    employeePerformanceService.options({ accessOnly: 1 }).then(() => { this.employeePerformanceEnabled = true; }).catch(() => {});
  },
  beforeUnmount() { this.requestId += 1; },
  methods: {
    ...mapMutations(['setReportType']),
    categoryLabel(category) {
      const key = categoryKey(category.name);
      return key ? this.$t(`reportCatalog.categories.${key}`) : this.$t(category.name);
    },
    isOperations(category) {
      return categoryKey(category.name) === 'operations' || categoryKey(this.$t(category.name)) === 'operations';
    },
    reportTitle(item) {
      if (item.id === 'requested-reports') return this.$t('reportParity.requestedReports');
      if (item.id === 'billing-diagnostics') return this.$t('reportParity.diagnostics');
      if (item.builtin) return this.$t(item.id === 'employee-performance' ? 'employeePerformance.title' : `reportCatalog.builtins.${item.id}.title`);
      const definition = reportDefinition(item.name);
      return definition ? this.$t(`reportCatalog.reports.${definition.key}.title`) : this.$t(item.name || 'reportCatalog.untitled');
    },
    reportDescription(item) {
      if (item.id === 'requested-reports') return this.$t('reportParity.requestedDescription');
      if (item.id === 'billing-diagnostics') return this.$t('reportParity.diagnosticsNote');
      if (item.builtin) return this.$t(item.id === 'employee-performance' ? 'reportCatalog.employeeDescription' : `reportCatalog.builtins.${item.id}.description`);
      const definition = reportDefinition(item.name);
      return definition ? this.$t(`reportCatalog.reports.${definition.key}.description`) : this.$t(item.description || 'reportCatalog.defaultDescription');
    },
    reportIcon(item) {
      if (item.builtin) return item.icon || 'groups';
      const definition = reportDefinition(item.name);
      return definition ? definition.icon : 'description';
    },
    handleOpen(item) { this.$refs.reportTypeModal.open(item); },
    handleAdd() { this.$refs.reportTypeModal.open({}); },
    handleSelect(item) {
      if (item.id === 'requested-reports') { this.$router.push('/documents/reports/reports'); return; }
      if (item.builtin) { this.$router.push(`/reports/${item.id || 'employee-performance'}`); return; }
      if (item.type === 'dynamic') {
        this.setReportType(item);
        this.$router.push(`/documents/reports/entity/${item.id}`);
        return;
      }
      this.$router.push({ path: '/documents/reports/reports', query: { per_page: 25, page: 1,
        filter: [{ type: 'eq', field: 'reportType', value: item.id }] } });
    },
    async reload() {
      this.loadItems();
      if (!this.reportAccess.categories) return;
      try {
        const { items } = await this.$service.category.getAll({ per_page: 100, page: 1,
          filter: [{ type: 'eq', field: 'state', value: 'active' }] });
        this.categories = items;
      } catch { this.loadError = true; }
    },
    async loadItems() {
      this.requestId += 1;
      const requestId = this.requestId;
      this.items = [];
      this.loadError = false;
      if (!this.reportAccess.reportTypes || String(this.tab).startsWith('builtin-')) { this.isLoading = false; return; }
      const filter = [{ type: 'eq', field: 'state', value: 'active' }];
      if (!['all', 'requested'].includes(this.tab)) filter.push({ type: 'eq', field: 'category', value: this.tab });
      this.isLoading = true;
      try {
        // The small catalogue is searched locally so translated names are searchable too.
        let page = 1;
        let totalPages = 1;
        do {
          // Pagination is sequential; stop as soon as the category changes.
                    const response = await this.$service.reportType.getAll({ per_page: 100, page, filter });
          if (requestId !== this.requestId) return;
          this.items = [...this.items, ...response.items];
          totalPages = Number(response.totalPages) || 1;
          page += 1;
        } while (page <= totalPages);
      } catch {
        if (requestId === this.requestId) this.loadError = true;
      } finally {
        if (requestId === this.requestId) this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.report-catalog { max-width: none; margin: 0 auto; color: var(--q-dark, #263238); }
.report-catalog__heading { display: flex; align-items: center; justify-content: space-between; gap: 20px; margin-bottom: 16px; }
.report-catalog__heading h1 { margin: 0 0 6px; font-size: 24px; line-height: 1.3; font-weight: 600; }
.report-catalog__heading p { margin: 0; color: #647078; font-size: 14px; line-height: 1.6; }
.report-catalog__panel { border: 0; border-radius: 0; background: transparent; }
.report-catalog__toolbar { display: flex; align-items: center; justify-content: space-between; gap: 20px; padding: 0 0 12px; }
.report-catalog__tabs { min-width: 0; color: #647078; }
.report-catalog__search { flex: 0 0 260px; }
.report-catalog__content { padding: 16px 0 0; }
.report-catalog__grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
.report-card { position: relative; display: flex; border: 1px solid #e0e5e9; border-radius: 0; background: white; transition: border-color .15s; }
.report-card:hover, .report-card:focus-within { border-color: var(--q-primary); background: #fafafa; }
.report-card__link { width: 100%; border: 0; border-radius: 0; background: transparent; padding: 16px; display: flex; flex-direction: column; text-align: left; font: inherit; color: inherit; cursor: pointer; }
.report-card__link:focus-visible { outline: 2px solid var(--q-primary); outline-offset: 3px; }
.report-card__top { display: flex; width: 100%; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 12px; }
.report-card__icon { display: inline-flex; align-items: center; justify-content: center; width: 24px; height: 24px; border-radius: 0; color: var(--q-primary); }
.report-card__type { font-size: 11px; line-height: 1.4; color: #5b6770; padding: 0; border-radius: 0; }
.report-card h2 { font-size: 16px; line-height: 1.4; font-weight: 600; margin: 0 0 9px; }
.report-card p { font-size: 13px; line-height: 1.5; color: #647078; margin: 0 0 16px; }
.report-card__action { display: flex; align-items: center; gap: 8px; margin-top: auto; padding-right: 32px; font-size: 13px; font-weight: 500; color: var(--q-primary); }
.report-card__edit { position: absolute; right: 12px; bottom: 12px; color: #647078; }
.report-catalog__status { display: flex; gap: 12px; justify-content: center; align-items: center; padding: 32px; color: #647078; }
.report-catalog__empty { padding: 56px 20px; text-align: center; color: #647078; }
.report-catalog__empty h2 { font-size: 18px; font-weight: 500; color: #263238; margin-bottom: 8px; }
.report-catalog__empty p { font-size: 14px; }
@media (max-width: 1250px) { .report-catalog__grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } .report-catalog__toolbar { flex-wrap: wrap; } .report-catalog__search { flex-basis: 100%; } }
@media (max-width: 600px) { .report-catalog { padding: 16px; } .report-catalog__heading { align-items: flex-start; flex-direction: column; gap: 12px; margin-bottom: 12px; } .report-catalog__heading h1 { font-size: 23px; } .report-catalog__toolbar { padding: 0 0 12px; gap: 12px; } .report-catalog__tabs { width: 100%; } .report-catalog__content { padding: 12px 0 0; } .report-catalog__grid { grid-template-columns: minmax(0, 1fr); gap: 12px; } .report-card__link { padding: 20px; } }
</style>

<style scoped>
.report-catalog :deep(.q-card),
.report-catalog :deep(.q-table__container),
.report-catalog :deep(.q-field__control),
.report-catalog :deep(.q-btn),
.report-catalog :deep(.q-banner) { border-radius: 0; }
</style>
